import type { EqDataPoint } from './device/commands'
import { API_BASE_URL } from '@/config/api'
import { localizeApiResponse } from '@/api/auth'

export interface PubgPresetData {
  id: string
  name: string
  datapoint: EqDataPoint[]
  isCustom?: boolean
  createdAt?: number
}

export interface PubgListResponse {
  presets: PubgPresetData[]
  lastPreset: string
  defaultPreset: string
}

export interface PubgApiResponse<T = unknown> {
  code: number
  message: string
  data: T
}

type PubgAction = 'list' | 'add' | 'remove' | 'pin' | 'find' | 'reset' | 'updatePoint' | 'updateName' | 'setLast'

const PUBG_API_MAP: Record<PubgAction, string> = {
  list: '/pubg/list',
  add: '/pubg/addEqData',
  remove: '/pubg/deleteEqData',
  pin: '/pubg/setTop',
  find: '/pubg/findEqById',
  reset: '/pubg/reset',
  updatePoint: '/pubg/updatePointByFid',
  updateName: '/pubg/updateName',
  setLast: '/pubg/setLast',
}

function createPubgFormData(uuid: string, linktype: string, fields: Record<string, string> = {}): FormData {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('linktype', linktype)
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value))
  return formData
}

function formDataForLog(formData: FormData): Record<string, string> {
  const result: Record<string, string> = {}
  formData.forEach((value, key) => {
    result[key] = typeof value === 'string' ? value : `[File ${value.name}]`
  })
  return result
}

async function postPubg<T>(action: PubgAction, formData: FormData): Promise<PubgApiResponse<T>> {
  const path = PUBG_API_MAP[action]
  console.log(`[PUBG][API] -> POST ${path}`, formDataForLog(formData))

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    body: formData,
    keepalive: action !== 'list' && action !== 'find',
  })

  if (!response.ok) {
    console.error(`[PUBG][API] <- ${path} HTTP ${response.status}`)
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = localizeApiResponse(await response.json() as PubgApiResponse<T>)
  console.log(`[PUBG][API] <- ${path}`, data)
  return data
}

function normalizePointForConfig(point: EqDataPoint, index: number) {
  return {
    fid: point.fid || index + 1,
    freq: String(point.freq).replace(/hz$/i, 'HZ'),
    gain: String(point.gain),
    q: String(point.q || '0.75'),
  }
}

export function getPubgList(uuid: string, linktype = '1'): Promise<PubgApiResponse<PubgListResponse>> {
  return postPubg('list', createPubgFormData(uuid, linktype))
}

export function addPubgPreset(
  uuid: string,
  preset: PubgPresetData,
  linktype = '1',
): Promise<PubgApiResponse<{ id?: string; presetId?: string; eqId?: string } | string | number | null>> {
  const config = {
    id: String(preset.id),
    name: String(preset.name),
    datapoint: preset.datapoint.map(normalizePointForConfig),
  }
  return postPubg('add', createPubgFormData(uuid, linktype, { config: JSON.stringify(config) }))
}

export function removePubgPreset(uuid: string, presetId: string, linktype = '1'): Promise<PubgApiResponse> {
  return postPubg('remove', createPubgFormData(uuid, linktype, { presetId }))
}

export function pinPubgPreset(uuid: string, presetId: string, linktype = '1'): Promise<PubgApiResponse> {
  return postPubg('pin', createPubgFormData(uuid, linktype, { presetId }))
}

export function findPubgById(uuid: string, presetId: string, linktype = '1'): Promise<PubgApiResponse<PubgPresetData>> {
  return postPubg('find', createPubgFormData(uuid, linktype, { presetId }))
}

export function resetPubgPreset(uuid: string, presetId: string, linktype = '1'): Promise<PubgApiResponse> {
  return postPubg('reset', createPubgFormData(uuid, linktype, { presetId }))
}

export function updatePubgPoint(
  uuid: string,
  presetId: string,
  point: EqDataPoint,
  linktype = '1',
): Promise<PubgApiResponse> {
  return postPubg('updatePoint', createPubgFormData(uuid, linktype, {
    presetId,
    fid: String(point.fid),
    newFreq: String(point.freq).replace(/hz$/i, 'hz'),
    newGain: String(point.gain),
  }))
}

export function setLastPubgPreset(uuid: string, presetId: string, linktype = '1'): Promise<PubgApiResponse<PubgListResponse>> {
  return postPubg('setLast', createPubgFormData(uuid, linktype, { presetId }))
}

export function renamePubgPreset(
  uuid: string,
  presetId: string,
  newName: string,
  linktype = '1',
): Promise<PubgApiResponse<PubgListResponse>> {
  return postPubg('updateName', createPubgFormData(uuid, linktype, { presetId, newName }))
}
